import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { FilterType, IFilterOption } from '../../../../interfaces/Filter.interfaces';
import { filterSlice } from '../../../../store/filterSlice';
import { setBrandScrollAnimationExecuted } from '../../../../store/scrollYSlice';
import { BrandIconFilterShortVer } from '../FilterSlideModal/components/FilterController/components/BrandIconFilterShortVer';

interface IBrandFilterToggleSectionProps {
  filterType: FilterType;
  style?: React.CSSProperties;
}

export const BrandFilterToggleSection = ({ filterType, style }: IBrandFilterToggleSectionProps) => {
  const dispatch = useAppDispatch();
  const filterStates = useAppSelector((state) => state.filter[filterType]);
  const brandScrollAnimationExecuted = useAppSelector((state) => state.scrollY.brandScrollAnimationExecuted);
  const filterActions = filterSlice.actions;
  const brandListRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // 브랜드 필터 찾기
  const brandFilter = filterStates.globalFilters.find((filter) => filter.key === 'filter_brand');

  // 브랜드 데이터가 로드된 후 스크롤 애니메이션 실행 - 페이지별로 1회만 실행
  useEffect(() => {
    // 브랜드 필터가 없거나 옵션이 없으면 애니메이션 실행하지 않음

    // 현재 페이지에서 이미 브랜드 애니메이션이 실행되었는지 확인
    if (brandScrollAnimationExecuted[pathname]) {
      return;
    }

    // 브랜드 데이터가 로드된 후 DOM이 업데이트될 때까지 대기
    const executeAnimation = () => {
      if (brandListRef.current) {
        // 스크롤 가능한지 확인
        const isScrollable = brandListRef.current.scrollWidth > brandListRef.current.clientWidth;

        if (isScrollable) {
          // 처음에 오른쪽으로 스크롤
          brandListRef.current.scrollLeft = 100;

          // 0.2초 후 원래 위치로 부드럽게 스크롤
          setTimeout(() => {
            if (brandListRef.current) {
              brandListRef.current.scrollTo({
                left: 0,
                behavior: 'smooth',
              });
            }
          }, 200);

          // 현재 페이지에서 브랜드 애니메이션이 실행되었음을 표시
          dispatch(setBrandScrollAnimationExecuted({ pathname, executed: true }));
        }
      }
    };

    // 브랜드 데이터가 로드된 후 DOM이 준비될 때까지 대기
    const checkAndExecute = () => {
      if (brandListRef.current && brandListRef.current.children.length > 0) {
        // DOM이 준비되었으면 애니메이션 실행
        executeAnimation();
      } else {
        // 아직 준비되지 않았으면 다시 시도
        setTimeout(checkAndExecute, 200);
      }
    };

    // 브랜드 데이터가 로드된 후 충분한 시간을 두고 실행
    setTimeout(checkAndExecute, 200);
  }, [brandFilter, pathname, brandScrollAnimationExecuted, dispatch, filterStates.isFilterOptionsAdded.brands]);

  if (!brandFilter || !brandFilter.options || brandFilter.options.length === 0) {
    return null;
  }

  const handleBrandClick = (option: IFilterOption) => {
    ampli.pvmClickFilterBrandQuickButton({
      filter_type: filterType,
      brand_name: option.optionName,
      changed_to: option.isSelected ? 'unselected' : 'selected',
    });

    dispatch(
      filterActions.changeFilterOptionIsSelected({
        filterType,
        targetFilter: brandFilter,
        targetOption: option,
      }),
    );
  };

  return (
    <Container style={style}>
      <BrandList ref={brandListRef}>
        {brandFilter.options.map((option) => (
          <BrandIconFilterShortVer
            key={`${brandFilter.key}-${option.optionName}-${option.value}`}
            filter={brandFilter}
            filterType={filterType}
            option={option}
            handleClick={() => handleBrandClick(option)}
            localFilters={filterStates.globalFilters}
          />
        ))}
        <BlurSection />
      </BrandList>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding-top: 8px;
  position: relative;
`;

const BrandList = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 16px 8px 16px;

  /* 스크롤바 숨기기 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const BlurSection = styled.div`
  display: flex;
  width: 40px;
  height: 96px;
  align-items: center;
  gap: 10px;

  position: absolute;
  right: 0;
  top: 0;

  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 64%);
`;
